import { initializeApp } from 'firebase/app'
import { Capacitor } from '@capacitor/core'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import {
  GoogleAuthProvider,
  OAuthProvider,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  getRedirectResult,
  initializeAuth,
  indexedDBLocalPersistence,
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  linkWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  unlink as fbUnlink,
  type AuthCredential,
  type User,
} from 'firebase/auth'
import { connectStorageEmulator, getStorage } from 'firebase/storage'
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore'

import { isConfigured as computeIsConfigured, resolveFirebaseOptions, usingEmulator } from './lib/config'

const env = import.meta.env

export const USE_EMULATOR = usingEmulator(env)
export const isConfigured = computeIsConfigured(env)

const app = initializeApp(resolveFirebaseOptions(env))

export const auth = initializeAuth(app, {
  // IndexedDB first so the session survives a restart in Electron and the Android WebView.
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
  // Required: initializeAuth (unlike getAuth) installs no default resolver, and
  // signInWithPopup/-Redirect throw auth/argument-error without one.
  popupRedirectResolver: browserPopupRedirectResolver,
})

/**
 * Offline support: Firestore keeps a durable local copy, serves reads from it
 * when the network is gone, and replays queued writes on reconnect. This is
 * what makes the app usable on a plane and what keeps all three shells honest.
 */
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})

/** Image attachments live in Cloud Storage; Firestore only holds their URLs. */
export const storage = getStorage(app)

if (USE_EMULATOR) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectStorageEmulator(storage, '127.0.0.1', 9199)
}

const provider = new GoogleAuthProvider()
provider.setCustomParameters({ prompt: 'select_account' })

/**
 * Google refuses OAuth inside embedded WebViews, so neither a popup nor a
 * redirect works on Android. The native plugin runs the real Google Sign-In
 * flow and hands back a credential, which we then exchange for a JS SDK
 * session so the rest of the app is unchanged.
 */
const isNative = Capacitor.isNativePlatform()

async function signInNative(): Promise<void> {
  const result = await FirebaseAuthentication.signInWithGoogle()
  const idToken = result.credential?.idToken
  if (!idToken) throw new Error('Google sign-in returned no ID token.')
  await signInWithCredential(auth, GoogleAuthProvider.credential(idToken, result.credential?.accessToken))
}

const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')
appleProvider.addScope('name')

/**
 * The project allows one account per email address, so signing in with a second
 * provider on an address that already exists fails with
 * auth/account-exists-with-different-credential instead of creating a duplicate.
 * The credential is parked here so it can be linked once the person proves the
 * account is theirs by signing in with the method they already have.
 */
export interface PendingLink { email: string; credential: AuthCredential; methods: string[] }
let pendingLink: PendingLink | null = null

export const getPendingLink = (): PendingLink | null => pendingLink
export const clearPendingLink = (): void => { pendingLink = null }

export const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'apple.com': 'Apple',
  password: 'email and password',
}

async function capturePendingLink(err: unknown): Promise<boolean> {
  const code = (err as { code?: string })?.code ?? ''
  if (!code.includes('account-exists-with-different-credential')) return false
  const credential =
    OAuthProvider.credentialFromError(err as never) ?? GoogleAuthProvider.credentialFromError(err as never)
  const email = (err as { customData?: { email?: string } })?.customData?.email ?? ''
  if (!credential || !email) return false
  // With email-enumeration protection on this can come back empty; the UI then
  // asks the person to use whichever method they set up, without naming it.
  const methods = await fetchSignInMethodsForEmail(auth, email).catch(() => [] as string[])
  pendingLink = { email, credential, methods }
  return true
}

/** Attaches a parked credential once the person is signed in. */
export async function completePendingLink(): Promise<boolean> {
  if (!pendingLink || !auth.currentUser) return false
  const { credential } = pendingLink
  pendingLink = null
  await linkWithCredential(auth.currentUser, credential).catch(() => undefined)
  return true
}

export function linkedProviders(): string[] {
  return auth.currentUser?.providerData.map((p) => p.providerId) ?? []
}

export async function linkProvider(id: 'google.com' | 'apple.com'): Promise<void> {
  if (!auth.currentUser) throw new Error('Not signed in.')
  const p = id === 'google.com' ? provider : appleProvider
  await linkWithPopup(auth.currentUser, p)
}

export async function linkPassword(password: string): Promise<void> {
  const user = auth.currentUser
  if (!user?.email) throw new Error('Not signed in.')
  await linkWithCredential(user, EmailAuthProvider.credential(user.email, password))
}

export async function unlinkProvider(id: string): Promise<void> {
  if (!auth.currentUser) throw new Error('Not signed in.')
  await fbUnlink(auth.currentUser, id)
}

async function popupOrRedirect(p: GoogleAuthProvider | OAuthProvider): Promise<void> {
  try {
    await signInWithPopup(auth, p)
    await completePendingLink()
  } catch (err: unknown) {
    if (await capturePendingLink(err)) throw err
    const code = (err as { code?: string })?.code ?? ''
    if (code.includes('popup-blocked') || code.includes('operation-not-supported')) {
      return signInWithRedirect(auth, p)
    }
    if (code.includes('popup-closed-by-user') || code.includes('cancelled-popup-request')) return
    throw err
  }
}

/** Apple requires its own credential exchange on a native shell. */
export async function signInWithApple(): Promise<void> {
  if (isNative) {
    const result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true })
    const idToken = result.credential?.idToken
    if (!idToken) throw new Error('Apple sign-in returned no ID token.')
    const credential = appleProvider.credential({ idToken, rawNonce: result.credential?.nonce })
    await signInWithCredential(auth, credential)
    return
  }
  return popupOrRedirect(appleProvider)
}

/**
 * Creating an account leaves the address unverified, and the security rules
 * require email_verified — so the verification mail goes out immediately and
 * the app holds the user on a "confirm your email" screen until it is done.
 */
export async function signUpWithEmail(email: string, password: string): Promise<void> {
  const cred = await createUserWithEmailAndPassword(auth, email.trim(), password)
  await sendEmailVerification(cred.user).catch(() => undefined)
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email.trim(), password)
  await completePendingLink()
}

export async function resendVerification(): Promise<void> {
  if (auth.currentUser) await sendEmailVerification(auth.currentUser)
}

export async function sendReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email.trim())
}

/** Re-reads the account so a just-confirmed address is picked up. */
export async function refreshUser(): Promise<boolean> {
  if (!auth.currentUser) return false
  await auth.currentUser.reload()
  await auth.currentUser.getIdToken(true)
  return auth.currentUser.emailVerified
}

/** Turns Firebase's error codes into something worth reading. */
export function authErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code ?? ''
  const map: Record<string, string> = {
    'auth/invalid-email': 'That email address doesn’t look right.',
    'auth/missing-password': 'Enter a password.',
    'auth/weak-password': 'Use at least six characters.',
    'auth/email-already-in-use': 'There’s already an account with that email. Try signing in.',
    'auth/invalid-credential': 'Email or password is incorrect.',
    'auth/wrong-password': 'Email or password is incorrect.',
    'auth/user-not-found': 'No account with that email yet. Create one below.',
    'auth/too-many-requests': 'Too many attempts. Wait a minute and try again.',
    'auth/popup-blocked': 'Your browser blocked the sign-in window.',
    'auth/account-exists-with-different-credential':
      'That email already has an account. Sign in with your original method to link this one.',
    'auth/credential-already-in-use': 'That account is already linked to someone else.',
    'auth/provider-already-linked': 'That sign-in method is already on your account.',
    'auth/requires-recent-login': 'Sign in again before changing your sign-in methods.',
    'auth/operation-not-allowed': 'That sign-in method isn’t enabled for this project.',
  }
  return map[code] ?? (err as Error)?.message ?? 'Something went wrong. Try again.'
}

export async function signIn(): Promise<void> {
  if (isNative) return signInNative()
  try {
    await signInWithPopup(auth, provider)
  } catch (err: unknown) {
    const code = (err as { code?: string })?.code ?? ''
    // Blocked popups and WebViews that refuse window.open fall back to a redirect.
    if (code.includes('popup-blocked') || code.includes('operation-not-supported')) {
      return signInWithRedirect(auth, provider)
    }
    if (code.includes('popup-closed-by-user') || code.includes('cancelled-popup-request')) return
    throw err
  }
}

export async function signOut(): Promise<void> {
  // Clear the native Google session too, or the next sign-in silently reuses it.
  if (isNative) await FirebaseAuthentication.signOut().catch(() => undefined)
  await fbSignOut(auth)
}

/** Must run once at startup so a redirect sign-in completes. */
export function consumeRedirect(): Promise<User | null> {
  return getRedirectResult(auth).then((r) => r?.user ?? null).catch(() => null)
}
